import { FC, useContext, useEffect, useRef } from 'react'
import jsLogger from 'js-logger'

import PCMAudioCapture from 'shared/components/recorder/PCMAudioCapture'
import StreamContext from 'shared/contexts/StreamContext'

// Server-side STT: the client does NOT transcribe. It captures the microphone as
// raw PCM and tees each frame to the socket via `onRawAudio`; the socket server
// runs the speech engine (Deepgram or otherwise) and relays interim/final
// transcripts back over its own channel. Provider-agnostic on purpose — the
// client is blind to which engine transcribes. This replaces running a client
// transcriber (e.g. DeepgramTranscriber) in capture-only mode.
type Props = {
  // Tee of each raw PCM frame (mono linear16) to the socket.
  onRawAudio: (pcm: ArrayBuffer, sampleRate: number) => void
  // Cold-start latency hook (kept for parity with the client Transcriber). The
  // STT link here is the socket, which is already up, so we report immediately.
  onConnected?: (latency: number, connectionNum: number) => void
  // Connection indicator. The socket owns the real STT link and is already
  // connected, so we report connected synthetically once capture starts.
  onConnectionStatusChanged?: (isConnected: boolean) => void
}

const ServerTranscriber: FC<Props> = ({
  onRawAudio,
  onConnected,
  onConnectionStatusChanged
}) => {
  const { stream } = useContext(StreamContext)
  const captureRef = useRef<PCMAudioCapture>()
  const mountedAtRef = useRef(Date.now())

  useEffect(() => {
    return () => {
      captureRef.current?.stop()
    }
  }, [])

  useEffect(() => {
    if (stream) {
      jsLogger.log('SERVER TRANSCRIBER INIT (capture-only)')
      captureRef.current?.stop()
      captureRef.current = new PCMAudioCapture(stream, onRawAudio)
      // The socket is the STT link and it's already up — surface "connected"
      // synthetically so the UI indicator isn't stuck.
      onConnectionStatusChanged?.(true)
      onConnected?.(Date.now() - mountedAtRef.current, 1)
    }
  }, [stream])

  return null
}

export default ServerTranscriber
